import { doc } from 'firebase/firestore';

import { getFirestore } from 'services/Firebase';
import { getAppBroker } from 'stores/AppBroker';
import { requireMatchAnyDocument } from 'stores/DatabasePaths';
import { DocumentCollectionType } from 'types/DocumentCollectionType';
import { DocumentType } from 'types/DocumentType';
import { splitFirestorePath } from 'utils/String';

import Breadcrumb from './Breadcrumb';
import type DocumentStore from './DocumentStore';
import Store from './Store';

let C2D_TYPE_LOOKUP: Map<string, DocumentType> | undefined;

function documentCollectionTypeToDocumentType(
    collectionType: string
): DocumentType | undefined {
    if (!C2D_TYPE_LOOKUP) {
        // Build a mapping of DocumentCollectionType values to
        // DocumentType, joining on shared keys.

        // for example, `DocumentCollectionType.PROJECT` ('projects' plural)
        // maps to `DocumentType.PROJECT` ('project' singular) because they
        // both have the key `PROJECT`.
        //
        // Note that there are exceptions to the plural -> singular pattern.
        // See the respective enums for details.

        C2D_TYPE_LOOKUP = new Map();
        for (const [dcKey, dcValue] of Object.entries(DocumentCollectionType)) {
            if (dcKey in DocumentType) {
                C2D_TYPE_LOOKUP.set(
                    dcValue,
                    DocumentType[dcKey as keyof typeof DocumentType]
                );
            }
        }
    }
    return C2D_TYPE_LOOKUP.get(collectionType);
}

class BreadcrumbManager extends Store {
    breadcrumbs: Breadcrumb[] = [];
    parent: DocumentStore<any, any>;
    crumbCount = 0;

    get all() {
        return this.breadcrumbs;
    }

    constructor(parent: DocumentStore) {
        super();
        this.parent = parent;
        this.becomeObservable();
    }

    load = async (path?: string, config?: { crumbCount?: number }) => {
        this.crumbCount = config?.crumbCount ?? 0;
        if (!path) path = this.parent.docRef?.path;

        // original returned [] on this path, but the happy path just
        // returned void. I don't think the return value was ever used.
        if (!path) return;

        const pathParts = splitFirestorePath(path);

        // Create a Breadcrumb object for each collectionName/documentId
        // pair that makes up the path
        const breadcrumbs = await Promise.all(
            pathParts.map(async ([bcColName, _bcDocName], i) => {
                const documentType =
                    documentCollectionTypeToDocumentType(bcColName) ??
                    DocumentType.DEFAULT;

                const trail = pathParts.slice(0, i + 1);

                // The database path is just the pathParts up to the current
                // document.
                let dbPath = trail.flat().join('/');

                // match the path before we append /public/info. idk
                const docCoord = requireMatchAnyDocument(dbPath);
                const linkUrl = getAppBroker().routeForDocument(docCoord);

                // Typically we want to access the ./public/info
                // sub-document of each object we query. Except for
                // tearsheets.
                if (documentType !== DocumentType.TEARSHEET) {
                    dbPath += '/public/info';
                }

                const docRef = doc(getFirestore(), dbPath);
                const newBreadcrumb = new Breadcrumb(this, docRef);
                newBreadcrumb.setUrl(linkUrl);
                newBreadcrumb.setType(documentType);
                await newBreadcrumb.load();
                if (!newBreadcrumb.exists) {
                    console.error(
                        `BreadcrumbManager.load: no document at ${dbPath}`
                    );
                }
                return newBreadcrumb;
            })
        );

        // After creating all breadcrumbs, limit them based on crumbCount
        // This is done by dropping the lowest indices (closest to the root)
        // first.
        if (this.crumbCount > 0) {
            this.breadcrumbs = breadcrumbs.slice(-this.crumbCount);
        } else {
            // Consider 0 to mean 'no limit'
            this.breadcrumbs = breadcrumbs;
        }
    };

    update = async () => {
        this.load();
    };

    reset = () => {
        this.breadcrumbs = [];
    };
}

export default BreadcrumbManager;
